import styled from "@emotion/styled";
import { useResizeObserver } from "@energyx-dev/energyx-ui";
import { useMutation } from "@tanstack/react-query";
import { patchVerificationFromFindPassword } from "api/password";
import { patchVerification } from "api/verification";
import { DEVICE } from "constants/device";
import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

type TVerification = {
  type: "sms" | "email";
  code: string;
  memberType: "normal" | "corporation";
  verificationId: string;
};

export const Verification = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const device = useResizeObserver();
  const [forRender, setForRender] = useState(false);

  const onSucessVerificationSuccess = () => {
    setForRender(true);
    window.location.href = "/verification/completed";
  };

  const onErrorVerificationError = () => {
    window.location.href = "/verification/expired";
  };

  const { mutateAsync: mutateVerification, isLoading } = useMutation(patchVerification, {
    onSuccess: onSucessVerificationSuccess,
    onError: onErrorVerificationError,
  });

  const { mutateAsync: mutateFindPasswordVerification, isLoading: findPwdLoading } = useMutation(
    patchVerificationFromFindPassword,
    {
      onSuccess: onSucessVerificationSuccess,
      onError: onErrorVerificationError,
    }
  );

  useEffect(() => {
    const type = searchParams.get("type");
    const verification = searchParams.get("verification");
    const code = searchParams.get("code");
    const action = searchParams.get("action");
    const verificationId = searchParams.get("id");

    const commonMutationParams: TVerification = {
      code: code as string,
      memberType: type === "NORMAL" ? "normal" : "corporation",
      type: verification === "sms" ? "sms" : "email",
      verificationId: verificationId || "",
    };

    if (action === "NORMAL") {
      mutateVerification(commonMutationParams);
    } else {
      mutateFindPasswordVerification(commonMutationParams);
    }
    if (location.pathname === "/verification/completed") {
      window.location.href = process.env.ENVIRONMENT!;
    }
  }, [location]);

  return (
    <Section isDesktop={device === DEVICE.DESKTOP}>
      {!isLoading && !findPwdLoading && forRender && (
        <article className="container">
          <>
            <Content>
              <span className="xicon-">envelope_opened</span>
              <h2>인증 완료</h2>
              <p>진행 중인 화면에서 다음 절차를 진행해주세요.</p>
            </Content>
          </>
        </article>
      )}
    </Section>
  );
};

const Section = styled.section<{ isDesktop: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 80px 0;
  height: ${({ isDesktop }) => (isDesktop ? "700px" : "488px")};
  article {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 64px;
  }

  ${({ theme }) => theme.breakpoints.down("DESKTOP")} {
    padding: 24px 16px 40px 16px;
    article {
      gap: 24px;

      > div:last-of-type {
        width: 100%;
      }
    }
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  text-align: center;

  > span {
    font-size: 136px;
    color: ${({ theme }) => theme.color.primary[60]};
  }

  > h2 {
    ${({ theme }) => theme.font.H2};
    color: ${({ theme }) => theme.color.black};
  }

  > p {
    ${({ theme }) => theme.font.Body1};
    color: ${({ theme }) => theme.color.mono[60]};
    white-space: pre-wrap;
  }

  ${({ theme }) => theme.breakpoints.down("DESKTOP")} {
    > span {
      font-size: 80px;
      color: ${({ theme }) => theme.color.primary[60]};
    }

    > h2 {
      ${({ theme }) => theme.font.Body3_h};
      color: ${({ theme }) => theme.color.black};
    }

    > p {
      ${({ theme }) => theme.font.Subtitle2};
      color: ${({ theme }) => theme.color.mono[60]};
      white-space: pre-wrap;
    }
  }
`;
